import React, { useState, useEffect, useMemo } from "react";
import MetaTags from "react-meta-tags";
import { useSelector } from "react-redux";
import { Modal, Alert } from "reactstrap";
import "../featureDesign.scss";
import FeatureKpiHeader from "../FeatureKpiHeader";
import { useRiskTrendHistory, severityCounters } from "../riskTrend";
import FeatureTableCard from "../FeatureTableCard";
import ScanProfileTabPane from "../ScanProfileTabPane";

// Some breach sources send already-masked values with block characters (e.g. "u███v"); show them as "*"
const BLOCK_CHARS = /[▀-▟]/g;
const unblock = (value) => (typeof value === "string" ? value.replace(BLOCK_CHARS, "*") : value);

const CredentialBreach = () => {
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [featureNameID, setfeatureNameID] = useState("");
  const [ScanProfileId, setScanProfileId] = useState("");
  const [Checks, setChecks] = useState([]);
  const [activeTab, setActiveTab] = useState("data");

  const [criticalCardPrice, setCriticalCardPrice] = useState(0);
  const [highCardPrice, setHighCardPrice] = useState(0);
  const [mediumCardPrice, setMediumCardPrice] = useState(0);
  const [lowCardPrice, setLowCardPrice] = useState(0);
  const [informationalCardPrice, setInformationalCardPrice] = useState(0);

  const [modalDetail, setModalDetail] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  // Privileged reveal states (in component state only; cleared on unmount)
  const [revealedSecrets, setRevealedSecrets] = useState({});
  const [revealLoading, setRevealLoading] = useState({});
  const [feedback, setFeedback] = useState(null);

  const userID = localStorage.getItem("userID");
  const orgID = localStorage.getItem("orgID");
  const token = localStorage.getItem("token");
  const siteDomain = localStorage.getItem("domain") || "scanme.nmap.org";

  const role = (localStorage.getItem("role") || "").toLowerCase().trim();
  const isReadOnly = role === "read_only";
  const canReveal = role === "tenant_admin" || role === "super_admin";

  const { layoutMode } = useSelector((state) => ({
    layoutMode: state.Layout.layoutMode,
  }));
  const isDark =
    layoutMode === "dark" ||
    (typeof document !== "undefined" && document.body.getAttribute("data-layout-mode") === "dark");
  const theme = isDark ? "dark" : "light";

  // Real per-day history for the Risk trend chart
  const trendHistory = useRiskTrendHistory(165112129704651, severityCounters.flat, loading);

  // Clear revealed secrets on component unmount
  useEffect(() => {
    return () => {
      setRevealedSecrets({});
    };
  }, []);

  useEffect(() => {
    async function checkAuthAndLoad() {
      try {
        if (token) {
          const authRes = await fetch(`${process.env.REACT_APP_DEFAULTPATH}jwtauth`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          });
          const authData = await authRes.json();
          if (authData && authData.status === false) {
            window.location.href = "/login";
            return;
          }
        }
      } catch (e) {
        console.warn("Auth check error:", e);
      }
      getCredentialBreachData();
      getScanProfileString();
    }
    checkAuthAndLoad();
  }, []);

  async function getCredentialBreachData() {
    setLoading(true);
    const featureIDForServer = 165112129704651;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}taskData`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: JSON.stringify({ userID, orgID, featureIDForServer, source: "credentials_page" }),
      });
      const data = await response.json();

      // Handle summary response (e.g. for read_only role)
      if (data && data.data && data.data.summary) {
        const s = data.data.summary;
        setCriticalCardPrice(s.Critical || 0);
        setHighCardPrice(s.High || 0);
        setMediumCardPrice(s.Medium || 0);
        setLowCardPrice(s.Low || 0);
        setInformationalCardPrice(s.Informational || 0);
      }

      if (data && data.data && Array.isArray(data.data.Data) && data.data.Data.length > 0) {
        const rawList = data.data.Data;
        let c = 0, h = 0, m = 0, l = 0, inf = 0;

        const mapped = rawList.map((item, index) => {
          const rawRisk = item.Risk || "Low";
          const rLower = String(rawRisk).toLowerCase();
          if (rLower.includes("crit")) c++;
          else if (rLower.includes("high")) h++;
          else if (rLower.includes("med")) m++;
          else if (rLower.includes("info")) inf++;
          else l++;

          return {
            ID: item.ID || String(index + 1),
            Email: item.Email || "-",
            Username: unblock(item.UserName || item.Username || "-"),
            Password: unblock(item.Password || "-"),
            DatabaseName: item.Databse_Name || item.DatabaseName || "-",
            IP: item.IP || "-",
            Hashed_Password: unblock(item.Hashed_Password || "-"),
            Name: unblock(item.Name || "-"),
            Address: unblock(item.Address || "-"),
            Phone: unblock(item.Phone || "-"),
            Risk: rawRisk,
          };
        });

        setProductData(mapped);
        if (!data.data.summary) {
          setCriticalCardPrice(c);
          setHighCardPrice(h);
          setMediumCardPrice(m);
          setLowCardPrice(l);
          setInformationalCardPrice(inf);
        }
      } else {
        setProductData([]);
        if (!data?.data?.summary) {
          setCriticalCardPrice(0);
          setHighCardPrice(0);
          setMediumCardPrice(0);
          setLowCardPrice(0);
          setInformationalCardPrice(0);
        }
      }
    } catch (err) {
      console.error("Error fetching Credential Breach data:", err);
      setProductData([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleReveal(credentialId, email) {
    if (!canReveal || !credentialId) return;
    setRevealLoading((prev) => ({ ...prev, [credentialId]: true }));
    setFeedback(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}exposedcredentials/reveal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: JSON.stringify({ credentialId, email }),
      });
      const data = await response.json();
      if (response.status === 200 && data && data.status && data.plaintext) {
        setRevealedSecrets((prev) => ({ ...prev, [credentialId]: data.plaintext }));
        // Automatically re-mask after 10 seconds
        setTimeout(() => {
          setRevealedSecrets((prev) => {
            const next = { ...prev };
            delete next[credentialId];
            return next;
          });
        }, 10000);
      } else {
        setFeedback({
          type: "danger",
          message: data?.error || "Failed to reveal credential secret",
        });
      }
    } catch (err) {
      setFeedback({
        type: "danger",
        message: err.message || "Error revealing credential secret",
      });
    } finally {
      setRevealLoading((prev) => ({ ...prev, [credentialId]: false }));
    }
  }

  async function getScanProfileString() {
    const featureIDForServer = 165112129704651;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}updatesubdomaintable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userID, orgID, featureIDForServer }),
      });
      const data = await response.json();
      if (data && data.data) {
        setScanProfileId(data.data.ScanProfileID || "");
        setfeatureNameID(data.data.FeatureName || "");
        setChecks(data.data.Checks || []);
      }
    } catch (err) {
      console.warn("ScanProfile fetch error:", err);
    }
  }

  async function updateProfileString(string, id, riskValue, index, stringID, taskType) {
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}updatestring`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stringID,
          string,
          id,
          riskValue,
          index,
          taskType,
        }),
      });
      const data = await response.json();
      if (data.status === "ok") {
        getScanProfileString();
      }
    } catch (e) {
      console.error("Update string error:", e);
    }
  }

  const getBadgeClass = (sev) => {
    switch (String(sev).toLowerCase()) {
      case "critical": return "feature-badge badge-critical";
      case "high": return "feature-badge badge-high";
      case "medium":
      case "med": return "feature-badge badge-medium";
      case "low": return "feature-badge badge-low";
      case "informational":
      case "info": return "feature-badge badge-info";
      default: return "feature-badge badge-info";
    }
  };

  const totalFindings =
    criticalCardPrice + highCardPrice + mediumCardPrice + lowCardPrice + informationalCardPrice;

  const { score, riskLabel } = useMemo(() => {
    if (totalFindings === 0) return { score: 100, riskLabel: "Low" };
    const deduction =
      criticalCardPrice * 12 + highCardPrice * 6 + mediumCardPrice * 2 + lowCardPrice * 1;
    const rawScore = 100 - deduction;

    let calculatedScore = 98.42;
    let label = "Informational";

    if (criticalCardPrice > 0) {
      calculatedScore = Math.max(15, Math.min(39, rawScore));
      label = "Critical";
    } else if (highCardPrice > 0) {
      calculatedScore = Math.max(40, Math.min(59, rawScore));
      label = "High";
    } else if (mediumCardPrice > 0) {
      calculatedScore = Math.max(60, Math.min(84, rawScore));
      label = "Medium";
    } else if (lowCardPrice > 0) {
      calculatedScore = 98.42;
      label = "Low";
    } else {
      calculatedScore = 98.42;
      label = "Low";
    }

    return { score: calculatedScore, riskLabel: label };
  }, [totalFindings, criticalCardPrice, highCardPrice, mediumCardPrice, lowCardPrice]);

  const columns = [
    { key: "ID", label: "ID", isId: true },
    { key: "Email", label: "Email", isMono: true },
    { key: "Username", label: "Username", isMono: true },
    {
      key: "Password",
      label: "Password",
      isMono: true,
      render: (val, r) => {
        const isRevealed = Boolean(revealedSecrets[r.ID]);
        const displayVal = isRevealed ? unblock(revealedSecrets[r.ID]) : val || "-";
        return (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: "var(--font-mono)" }}>{displayVal}</span>
            {canReveal && (
              <button
                type="button"
                title={isRevealed ? "Password revealed (auto-remask in 10s)" : "Reveal password (privileged)"}
                className="btn-outline-action"
                style={{
                  padding: "2px 7px",
                  fontSize: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  lineHeight: 1,
                  borderRadius: 4,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReveal(r.ID, r.Email);
                }}
                disabled={revealLoading[r.ID]}
              >
                {revealLoading[r.ID] ? (
                  <i className="bx bx-loader-alt bx-spin" />
                ) : (
                  <i className={isRevealed ? "bx bx-show text-primary" : "bx bx-hide"} />
                )}
              </button>
            )}
          </div>
        );
      },
    },
    { key: "DatabaseName", label: "Database Name" },
    {
      key: "Risk",
      label: "Risk",
      render: (val) => <span className={getBadgeClass(val)}>{val || "Low"}</span>,
    },
    {
      key: "Actions",
      label: "Actions",
      render: (val, r) => (
        <button
          className="btn-outline-action"
          onClick={() => {
            setDetailItem(r);
            setModalDetail(true);
          }}
        >
          View Details
        </button>
      ),
    },
  ];

  return (
    <React.Fragment>
      <MetaTags>
        <title>Exposed Credentials | Glogix</title>
      </MetaTags>

      <div className="page-content feature-page-root" data-theme={theme}>
        <div className="feature-container">
          <FeatureKpiHeader
            title="Credential Details"
            breadcrumbItem="Credential Details"
            siteTitle={siteDomain}
            score={score}
            riskLabel={riskLabel}
            counts={{
              critical: criticalCardPrice,
              high: highCardPrice,
              medium: mediumCardPrice,
              low: lowCardPrice,
              info: informationalCardPrice,
            }}
            loading={loading}
            trendHistory={trendHistory}
          />

          {feedback && (
            <div style={{ marginBottom: 16 }}>
              <Alert color={feedback.type} toggle={() => setFeedback(null)} className="mb-0">
                {feedback.message}
              </Alert>
            </div>
          )}

          {isReadOnly ? (
            <div
              className="feature-table-card"
              style={{
                padding: "32px 24px",
                background: "var(--bg-surface)",
                border: "1px solid var(--border-default)",
                borderRadius: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <i className="bx bx-shield-quarter" style={{ fontSize: 24, color: "var(--accent-primary, #3b82f6)" }} />
                <h5 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Exposed Credentials Summary</h5>
              </div>
              <div
                style={{
                  padding: "12px 16px",
                  background: "rgba(59, 130, 246, 0.08)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: 6,
                  marginBottom: 24,
                  fontSize: 13,
                  color: "var(--fg-secondary)",
                }}
              >
                Detailed credential data is restricted for your role.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16 }}>
                <div style={{ padding: "16px", borderRadius: 6, background: "var(--bg-hover, rgba(255,255,255,0.03))", border: "1px solid var(--border-default)" }}>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>Total Exposed</div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{totalFindings}</div>
                </div>
                <div style={{ padding: "16px", borderRadius: 6, background: "var(--bg-hover, rgba(255,255,255,0.03))", border: "1px solid var(--border-default)" }}>
                  <div style={{ fontSize: 11, color: "var(--sev-critical, #ef4444)", textTransform: "uppercase" }}>Critical</div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: "var(--sev-critical, #ef4444)" }}>{criticalCardPrice}</div>
                </div>
                <div style={{ padding: "16px", borderRadius: 6, background: "var(--bg-hover, rgba(255,255,255,0.03))", border: "1px solid var(--border-default)" }}>
                  <div style={{ fontSize: 11, color: "var(--sev-high, #f97316)", textTransform: "uppercase" }}>High</div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: "var(--sev-high, #f97316)" }}>{highCardPrice}</div>
                </div>
                <div style={{ padding: "16px", borderRadius: 6, background: "var(--bg-hover, rgba(255,255,255,0.03))", border: "1px solid var(--border-default)" }}>
                  <div style={{ fontSize: 11, color: "var(--sev-medium, #eab308)", textTransform: "uppercase" }}>Medium</div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: "var(--sev-medium, #eab308)" }}>{mediumCardPrice}</div>
                </div>
                <div style={{ padding: "16px", borderRadius: 6, background: "var(--bg-hover, rgba(255,255,255,0.03))", border: "1px solid var(--border-default)" }}>
                  <div style={{ fontSize: 11, color: "var(--sev-low, #22c55e)", textTransform: "uppercase" }}>Low</div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: "var(--sev-low, #22c55e)" }}>{lowCardPrice}</div>
                </div>
              </div>
            </div>
          ) : (
            <FeatureTableCard
              tabs={[
                { key: "data", label: "Exposed Credentials", count: productData.length },
                { key: "profile", label: "Scan Profile" },
              ]}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              searchPlaceholder="Search"
              columns={columns}
              data={productData}
            >
              {activeTab === "profile" && (
                <ScanProfileTabPane
                  featureName={featureNameID || "Exposed Credentials Profile"}
                  checks={Checks}
                  onDelete={(id, keyword) =>
                    updateProfileString(keyword, ScanProfileId, "Low", 0, id, "delete")
                  }
                  onSave={(id, keyword, risk) =>
                    updateProfileString(keyword, ScanProfileId, risk, 0, id, "updateRisk")
                  }
                  onAdd={(keyword, risk) =>
                    updateProfileString(keyword, ScanProfileId, risk, 0, null, "add")
                  }
                />
              )}
            </FeatureTableCard>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {detailItem && (
        <Modal
          isOpen={modalDetail}
          toggle={() => setModalDetail(!modalDetail)}
          size="lg"
          centered
          className="feature-detail-modal"
        >
          <div className="modal-content" style={{ background: "var(--bg-surface)", color: "var(--fg-primary)", border: "1px solid var(--border-default)" }}>
            <div className="modal-header" style={{ borderBottom: "1px solid var(--border-default)", padding: "16px 20px" }}>
              <h5 className="modal-title" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                Breach Record Details
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => setModalDetail(false)}
                style={{ background: "none", border: "none", color: "var(--fg-secondary)", fontSize: 20, cursor: "pointer" }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>ID</div>
                  <div style={{ fontSize: 13, fontFamily: "var(--font-mono)" }}>{detailItem.ID}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>Risk</div>
                  <div>
                    <span className={getBadgeClass(detailItem.Risk)}>
                      {detailItem.Risk}
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>Email</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{detailItem.Email}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>Username</div>
                  <div style={{ fontSize: 13, fontFamily: "var(--font-mono)" }}>{detailItem.Username}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>Password</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ fontSize: 13, fontFamily: "var(--font-mono)", color: "var(--sev-critical)" }}>
                      {unblock(revealedSecrets[detailItem.ID]) || detailItem.Password || "-"}
                    </div>
                    {canReveal && (
                      <button
                        type="button"
                        className="btn-outline-action"
                        style={{ padding: "2px 7px", fontSize: 12, borderRadius: 4 }}
                        onClick={() => handleReveal(detailItem.ID, detailItem.Email)}
                        disabled={revealLoading[detailItem.ID]}
                        title={revealedSecrets[detailItem.ID] ? "Password revealed" : "Reveal password (privileged)"}
                      >
                        {revealLoading[detailItem.ID] ? (
                          <i className="bx bx-loader-alt bx-spin" />
                        ) : (
                          <i className={revealedSecrets[detailItem.ID] ? "bx bx-show text-primary" : "bx bx-hide"} />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>Database Name</div>
                  <div style={{ fontSize: 13 }}>{detailItem.DatabaseName}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>IP Address</div>
                  <div style={{ fontSize: 13, fontFamily: "var(--font-mono)" }}>{detailItem.IP}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>Full Name</div>
                  <div style={{ fontSize: 13 }}>{detailItem.Name}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>Phone</div>
                  <div style={{ fontSize: 13 }}>{detailItem.Phone}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-tertiary)", textTransform: "uppercase" }}>Address</div>
                  <div style={{ fontSize: 13 }}>{detailItem.Address}</div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ borderTop: "1px solid var(--border-default)", padding: "12px 20px" }}>
              <button
                className="feature-btn-refresh"
                onClick={() => setModalDetail(false)}
                style={{ padding: "6px 14px" }}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default CredentialBreach;
